import React, { useState, useEffect } from "react";
import {TextField, Grid, CircularProgress, MenuItem} from "@mui/material";
import axios from "axios";
import { showToast } from '../../utils/toastUtils';
import ActionButton from "../../components/ActionButton";
import { useSelector } from 'react-redux';

const industries = [
  "Technology and IT",
  "Healthcare and Pharmaceuticals",
  "Finance and Banking",
  "E-commerce and Retail",
  "Energy and Utilities",
  "Manufacturing and Automotive",
  "Telecommunications",
  "Construction and Real Estate",
  "Entertainment and Media",
  "Education and Training",
  "Food and Beverage",
  "Agriculture and Agribusiness",
  "Aerospace and Defense",
  "Travel and Tourism",
  "Fashion and Apparel",
  "Mining and Metals",
  "Chemicals and Materials",
  "Logistics and Supply Chain",
  "Consulting and Professional Services",
  "Environmental and Sustainability",
];

const sizes = [
  "0-10",
  "11-20",
  "21-50",
  "51-100",
  "101-500",
  "501-1000",
  "1001-5000",
  "5001+",
];

const UserDetailsForm = () => {
  const userIdFromRedux = useSelector(state => state.onboarding.userId);
  const userId = userIdFromRedux || localStorage.getItem('userId');

  const [userData, setUserData] = useState(null); 
  const [loading, setLoading] = useState(false);
  const [initialUserData, setInitialUserData] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        showToast("User not found. Please log in again.", "error");
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/user/${userId}`
        );
        setUserData(response.data);
        setInitialUserData(response.data);
      } catch (err) {
        console.error("Failed to load user data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setUserData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const changedFields = Object.keys(userData).filter(key => userData[key] !== initialUserData[key]);

    if (changedFields.length === 0) {
      showToast("No changes", "info");
      return;
    }

    try {
      const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/user/profile`, {
        oldUserProfile: initialUserData,
        newUserProfile: userData
      });

      if (response.data.message === 'No fields were updated') {
        showToast("No changes detected ", "info");
      } else {
        setUserData(response.data);
        setInitialUserData(response.data);
        showToast("User details updated successfully", "success");
      }
    } catch (err) {
      showToast("Error updating user data", "error");
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!userData) {
    return <p>No user data available</p>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={userData.firstName || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={userData.lastName || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={userData.email || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={userData.phone || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Organization Name"
            name="organisation"
            value={userData.organisation || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Industry"
            name="industry"
            value={userData.industry || ""}
            onChange={handleChange}
          >
            {industries.map((industry) => (
              <MenuItem key={industry} value={industry}>
                {industry}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Organization Size"
              name="size"
              value={userData.size || ""}
              onChange={handleChange}
            >
              {sizes.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Website"
            name="website"
            value={userData.website || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <ActionButton
            text={loading ? <CircularProgress size={24} /> : 'Save Changes'}
            onClick={handleSubmit}
          />
        </Grid>
      </Grid>
    </form>
  );
};

export default UserDetailsForm;
